import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { useLogin } from "@/hooks/mutations/auth";
import { Toaster } from 'sonner';
import { LogIn } from "lucide-react";
import { motion } from "framer-motion";
import { authService } from "@/services/authService";

export function Login() {
  const navigate = useNavigate();
  // const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginMutation = useLogin({
    onSuccess: ({ data }) => {
      if (!data) {
        throw new Error("Invalid response data");
      }

      // Save the entire login response to localStorage
      localStorage.setItem('loginResponse', JSON.stringify(data));

      // Role-based redirect logic
      const userRole = data.user.roles[0].title;
      const userFlow = data.user.status || 'in-person';
      const paymentStatus = data.user.payment_status;
      
      // Handle payment status for remote users
      if (userRole === "User" && userFlow === "remote" && paymentStatus) {
        if (paymentStatus === "pending") {
          // Redirect to paywall
          navigate("/paywall");
          return;
        } else if (paymentStatus === "failed") {
          // Show cool payment expired message and redirect to welcome video
          toast.error("Your previous booking attempt has expired. Please book another slot.");
          navigate("/welcome-video");
          return;
        }
      }
      
      // Show success toast for all other cases
      // toast.success("Login successful!");
      
      // Default role-based redirects
      switch(userRole) {
        case "Admin":
          navigate("/admin-dashboard");
          break;
        case "Interviewer":
          navigate("/interviewer-dashboard");
          break;
        case "User":
        default:
          navigate("/user-dashboard");
          break;
      }
    },
    onError: (error) => {
      if (error?.response?.status === 422) {
        toast.custom((t) => (
          <div className="bg-[#F16745] text-white text-base px-6 py-4 rounded-lg shadow-md flex items-center gap-3 whitespace-nowrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Invalid email or password. Please try again.
          </div>
        ), {
          duration: 3000,
          position: "top-right",
          style: { zIndex: 9999 }
        });        
        return;
      }
    
      if (error?.response?.status === 401) {
        toast.error("", {
          description: "Invalid email or password. Please try again.",
          duration: 3000,
          position: "top-right",
        });
        return;
      }
    
      // Default error message
      toast.error("Login Failed", {
        description: error.message || "Something went wrong. Please try again.",
        duration: 3000,
        position: "top-right",
      });
    },
  });

  // const handleOtpRequest = async (email) => {
  //   setIsLoading(true);
  //   try {
  //     await authService.loginWithOtp({ email });
  //     toast.custom((t) => (
  //       <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
  //         <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  //           <path d="M20 6L9 17l-5-5"/>
  //         </svg>
  //         OTP sent successfully!
  //       </div>
  //     ), {
  //       duration: 2000,
  //       position: "top-right",
  //       style: {
  //         zIndex: 9999,
  //       }
  //     });
      
  //     setTimeout(() => {
  //       navigate("/otp-verification", { 
  //         state: { email }
  //       });
  //     }, 1000);
      
  //   } catch (error) {
  //     console.error('OTP Send Error:', error);
      
  //     if (error?.response?.status === 422) {
  //       const errorMessage = error.response?.data?.message || 
  //                          (error.response?.data?.errors?.email && error.response?.data?.errors?.email[0]) ||
  //                          "Invalid email address";
        
  //       toast.custom((t) => (
  //         <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
  //           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  //             <line x1="18" y1="6" x2="6" y2="18"></line>
  //             <line x1="6" y1="6" x2="18" y2="18"></line>
  //           </svg>
  //           {errorMessage}
  //         </div>
  //       ), {
  //         position: "top-right",
  //         style: {
  //           zIndex: 9999,
  //         }
  //       });
  //     } else {
  //       toast.custom((t) => (
  //         <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
  //           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  //             <line x1="18" y1="6" x2="6" y2="18"></line>
  //             <line x1="6" y1="6" x2="18" y2="18"></line>
  //           </svg>
  //           {error.response?.data?.message || "Failed to send OTP"}
  //         </div>
  //       ), {
  //         position: "top-right",
  //         style: {
  //           zIndex: 9999,
  //         }
  //       });
  //     }
  //     setIsLoading(false);
  //   }
  // };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      loginMutation.mutate({
        email: values.email,
        password: values.password,
      });
    },
  });

  return (
    <>
      <Toaster
        richColors
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
          duration: 3000,
        }}
      />
      <div className="min-h-screen bg-background flex items-center">
        <div className="max-w-md mx-auto px-4 w-full">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
            className="bg-secondary rounded-xl border border-secondary-border p-8"
        >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                <LogIn className="text-primary-foreground text-2xl" />
              </div>
              <h1 className="text-[1.25rem] font-semibold text-foreground mb-2">Welcome Back</h1>
              <p className="text-base font-normal text-muted-foreground">Sign in to access your Postscrypt account</p>
            </div>

            {/* Login Method Toggle - Temporarily Disabled */}
            {/* <div className="flex bg-muted rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setIsOtpLogin(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !isOtpLogin 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => setIsOtpLogin(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isOtpLogin 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                OTP
              </button>
            </div> */}

            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-semibold text-foreground mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                    placeholder="your@email.com" 
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-semibold text-foreground mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                    placeholder="••••••••" 
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="h-4 w-4 text-primary focus:ring-primary border-border rounded" />
                    <span className="ml-2 text-sm text-muted-foreground">Remember me</span>
                  </label>
                  {/* Forgot password button temporarily disabled */}
                  {/* <button 
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Forgot password?
                  </button> */}
                </div>

                <button 
                  type="submit" 
                  disabled={loginMutation.isPending || isLoading}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors"
                >
                  {loginMutation.isPending || isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </form>
        </motion.div>
        </div>
      </div>
    </>
  );
}
